/* eslint-disable max-lines */
//
//  SDKToolsJSONElementStyleDictionary.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { ColorToken } from "../../../exports";
import { TokenType } from "../../../model/enums/SDKTokenType";
import { sureOf } from "../../../utils/CommonUtils";
import { UnreachableCaseError } from "../../../utils/UnreachableCaseError";
import { TokenJSONElementTransformer } from "./SDKToolsJSONElementTransformer";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Configuration
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tool implementation
/** Token transformer that allow generic transform of tokens to different destinations, like Style Dictionary or Figma Tokens representation */
export class TokenJSONElementFigmaTokensTransformer extends TokenJSONElementTransformer {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Token Value Representation
    /** Represent color token value either as reference or as plain representation */
    representColorTokenValue(value, allTokens, allGroups, options) {
        let result;
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            return ColorToken.colorValueToHex8(value);
        }
        return result;
    }
    /** Represent dimension token value either as reference or as plain representation */
    representDimensionTokenValue(value, allTokens, allGroups, options) {
        let result;
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            result = `${value.measure}px`;
        }
        return result;
    }
    /** Represent text token value either as reference or as plain representation */
    representStringTokenValue(value, allTokens, allGroups, options) {
        let result;
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            result = value.text;
        }
        return result;
    }
    representOptionTokenValue(value, allTokens, allGroups, options) {
        let result;
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            result = value.value;
        }
        return result;
    }
    /** Represent typography token value either as reference or as plain representation */
    representTypographyTokenValue(value, allTokens, allGroups, options) {
        let result;
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            result = {
                fontSize: {
                    type: "measure",
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value: this.representDimensionTokenValue(value.fontSize, allTokens, allGroups, options),
                },
                textDecoration: value.textDecoration,
                textCase: value.textCase,
                letterSpacing: {
                    type: "measure",
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value: this.representDimensionTokenValue(value.letterSpacing, allTokens, allGroups, options),
                },
                paragraphIndent: {
                    type: "measure",
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value: this.representDimensionTokenValue(value.paragraphIndent, allTokens, allGroups, options),
                },
                paragraphSpacing: {
                    type: "measure",
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value: this.representDimensionTokenValue(value.paragraphSpacing, allTokens, allGroups, options),
                },
                lineHeight: value.lineHeight
                    ? {
                        type: "measure",
                        // TODO:fix-sdk-eslint
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        value: this.representDimensionTokenValue(value.lineHeight, allTokens, allGroups, options),
                    }
                    : undefined,
            };
        }
        return result;
    }
    /** Represent border token value either as reference or as plain representation */
    representBorderTokenValue(value, allTokens, allGroups, options) {
        let result;
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            result = {
                color: {
                    type: "color",
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value: this.representColorTokenValue(value.color, allTokens, allGroups, options),
                },
                width: {
                    type: "measure",
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value: this.representDimensionTokenValue(value.width, allTokens, allGroups, options),
                },
                position: {
                    type: "string",
                    value: value.position,
                },
            };
        }
        return result;
    }
    /** Represent shadow token value either as reference or as plain representation */
    representShadowTokenValue(values, allTokens, allGroups, options) {
        let result;
        const value = sureOf(values[0]); // FIXME: Layers
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            result = {
                color: {
                    type: "color",
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value: this.representColorTokenValue(value.color, allTokens, allGroups, options),
                },
                x: {
                    type: "measure",
                    value: value.x,
                },
                y: {
                    type: "measure",
                    value: value.y,
                },
                radius: {
                    type: "measure",
                    value: value.radius,
                },
                spread: {
                    type: "measure",
                    value: value.spread,
                },
                opacity: {
                    type: "size",
                    value: value.opacity,
                },
            };
        }
        return result;
    }
    /** Represent gradient token value either as reference or as plain representation */
    representGradientTokenValue(values, allTokens, allGroups, options) {
        let result;
        const value = sureOf(values[0]); // FIXME: Layers
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            result = {
                to: {
                    type: "point",
                    value: {
                        x: {
                            type: "size",
                            value: value.to.x,
                        },
                        y: {
                            type: "size",
                            value: value.to.y,
                        },
                    },
                },
                from: {
                    type: "point",
                    value: {
                        x: {
                            type: "size",
                            value: value.from.x,
                        },
                        y: {
                            type: "size",
                            value: value.from.y,
                        },
                    },
                },
                type: {
                    type: "string",
                    value: value.type,
                },
                aspectRatio: {
                    type: "size",
                    value: value.aspectRatio,
                },
                stops: {},
            };
            // Inject gradient stops
            let count = 0;
            for (const stop of value.stops) {
                const stopObject = {
                    type: "gradientStop",
                    position: {
                        type: "size",
                        value: stop.position,
                    },
                    color: {
                        type: "color",
                        // TODO:fix-sdk-eslint
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        value: this.representColorTokenValue(stop.color, allTokens, allGroups, options),
                    },
                };
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                result.stops[`${count}`] = stopObject;
                count += 1;
            }
        }
        return result;
    }
    /** Retrieve token wrapper containing its metadata and value information (used as container for each defined token) */
    tokenWrapper(token, value, options, groups) {
        const data = {
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            value,
            type: undefined,
            brandId: undefined,
            description: undefined,
        };
        if (options.includeType) {
            // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
            data.type = this.typeLabelConsideringAbstractGroups(token, groups);
        }
        if (options.includeBrandId) {
            // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'undefined... Remove this comment to see the full error message
            data.brandId = token.brandId;
        }
        if (options.includeComments) {
            // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
            data.description =
                token.description.length > 0 ? token.description : undefined;
        }
        return data;
    }
    /** Retrieve human-readable token type in unified fashion, used both as token type and as token master group */
    typeLabelConsideringAbstractGroups(token, groups) {
        const parentGroup = groups.find((group) => group.id === token.parentGroupId);
        // @ts-expect-error TS(2345): Argument of type 'TokenGroup | undefined' is not a... Remove this comment to see the full error message
        const groupChain = this.referenceGroupChain(parentGroup, groups);
        switch (token.tokenType) {
            case TokenType.color:
                return "color";
            // TODO: Is this correct?
            case TokenType.size:
                return "sizing";
            case TokenType.space:
                return "spacing";
            case TokenType.opacity:
                return "opacity";
            case TokenType.fontSize:
                return "fontSize";
            case TokenType.lineHeight:
                return "lineHeight";
            case TokenType.letterSpacing:
                return "letterSpacing";
            case TokenType.paragraphSpacing:
                return "paragraphSpacing";
            case TokenType.borderWidth:
                return "borderWidth";
            case TokenType.radius:
                return "borderRadius";
            // TODO: Should we throw an Error here or return duration and zindex or sizing?
            case TokenType.duration:
                return "duration";
            case TokenType.zIndex:
                return undefined;
            case TokenType.dimension: {
                if (groupChain.length >= 2) {
                    const group = sureOf(groupChain[1]);
                    switch (group.name // Checking by abstract, autogenerated group
                    ) {
                        case "Sizing":
                            return "sizing";
                        case "Font Size":
                            return "fontSize";
                        case "Border Radius":
                            return "borderRadius";
                        case "Border Width":
                            return "borderWidth";
                        case "Paragraph Spacing":
                            return "paragraphSpacing";
                        case "Line Height":
                            return "lineHeight";
                        case "Letter Spacing":
                            return "letterSpacing";
                        case "Spacing":
                            return "spacing";
                        case "Duration":
                            return "duration";
                        case "Opacity":
                            return "opacity";
                        case "Dimension":
                            return "dimension";
                        default:
                            return group.name;
                    }
                }
                return "sizing";
            }
            // TODO: Continue from here
            case TokenType.border:
                return "border";
            case TokenType.gradient:
                return "gradient";
            case TokenType.shadow:
                return "shadow";
            // TODO: Should it still be text for all of them?
            case TokenType.string:
            case TokenType.productCopy:
            case TokenType.fontFamily:
            case TokenType.fontWeight:
                return "text";
            case TokenType.textCase:
                return "textCase";
            case TokenType.textDecoration:
                return "textDecoration";
            case TokenType.visibility:
                return "visibility";
            case TokenType.typography:
                return "typography";
            // TODO: Should we throw an Error here or return blur and generic?
            case TokenType.blur:
                return undefined;
            default:
                throw new UnreachableCaseError(token.tokenType);
        }
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Object wrappers
    /** Retrieve wrapper to certain token (referenced by name) pointing to token value */
    referenceWrapper(reference) {
        return `{${reference}}`;
    }
    /** Retrieve safe group name made out of normal group name
     * This replace spaces with dashes, also change anything non-alphanumeric char to it as well.
     * For example, ST&RK Industries will be changed to st-rk-industries
     */
    safeGroupName(group) {
        return group.name;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Naming
    /** Create full reference name representing token. Such name can, for example, look like: [g1].[g2].[g3].[g4].[token-name] */
    referenceName(referenceId, allTokens, allGroups, options) {
        var _a;
        const referencedToken = allTokens.get(referenceId);
        if (!referencedToken) {
            throw new Error(`JS: Unable to find token with id: ${referenceId}`);
        }
        // Find the group to which token belongs. This is really suboptimal and should be solved by the SDK to just provide the group reference
        const parentGroup = allGroups.find((g) => referencedToken.parentGroupId === g.id);
        if (!parentGroup) {
            throw new Error(`JS: Unable to find group with id: ${referenceId}`);
        }
        const tokenPart = this.safeTokenName(referencedToken, options.naming);
        const groupParts = this.referenceGroupChain(parentGroup, allGroups).map((g) => this.safeGroupName(g));
        if (!options.includeRootTypeNodes) {
            groupParts.splice(0, 1);
        }
        // Drop first segment if needed - from pre-created mapping groups that are not available by default in Supernova which we are creating
        let dropFirstSegment = false;
        switch (referencedToken.tokenType) {
            case TokenType.dimension:
            case TokenType.space:
            case TokenType.size:
            case TokenType.opacity:
            case TokenType.fontSize:
            case TokenType.lineHeight:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.borderWidth:
            case TokenType.radius:
            case TokenType.duration:
            case TokenType.zIndex: // TODO: ?
                if ([
                    "Border Radius",
                    "Border Width",
                    "Sizing",
                    "Font Size",
                    "Paragraph Spacing",
                    "Line Height",
                    "Letter Spacing",
                    "Spacing",
                    "Opacity",
                    "Dimension",
                    "Duration",
                ].includes((_a = groupParts[0]) !== null && _a !== void 0 ? _a : "")) {
                    dropFirstSegment = true;
                }
                break;
            default:
                break;
        }
        if (dropFirstSegment) {
            groupParts.splice(0, 1);
        }
        return [...groupParts, tokenPart].join(".");
    }
    groupIsAbstract(group, allGroups) {
        // Abstract groups can only be right after the root, otherwise they are never abstract
        const parentId = group.parentGroupId;
        const parent = allGroups.find((g) => g.id === parentId);
        if (!parent || !parent.isRoot) {
            return false;
        }
        switch (group.tokenType) {
            // TODO: Should add size and others?
            // TODO: Should add text and others?
            case TokenType.dimension:
                if ([
                    "Border Radius",
                    "Border Width",
                    "Sizing",
                    "Font Size",
                    "Paragraph Spacing",
                    "Line Height",
                    "Letter Spacing",
                    "Spacing",
                    "Opacity",
                ].includes(group.name)) {
                    return true;
                }
                break;
            default:
                break;
        }
        return false;
    }
}
